import { DayPart, DayPartMenu } from "@/dto/dayPartMenu";
import { isEmpty, isNil } from "lodash";
import { DateTime, Interval } from "luxon";
import { HHMMtoDate, toDateTime } from "./dateTimeUtils";
import { daysOfWeek, WeekdayFull } from "@/constants/enum";
import { TFunction } from "@/types";
import getDayPartMenus from "@/dataLayer/getDayPartMenus";
import { redirect } from "next/navigation";
import { decodeHtmlEntities } from "./htmlEntityUtils";

const ALL_DAYS = daysOfWeek.map((day) => day.toUpperCase());

export const sortAndUpdateDayPartMenus = (
  dayPartMenus: DayPartMenu[],
  timezone: string,
  t: TFunction
) => {
  const now = DateTime.now().setZone(timezone);
  const currentDayIndex = now.weekday % 7;
  const dayName = daysOfWeek[now.weekday % 7];
  const formattedDayParts = formatMenus(dayPartMenus, t);
  const updatedMenus = updateMenusWithAvailabilityScore(
    formattedDayParts,
    currentDayIndex
  );
  const sortedDayPartMenus = sort(updatedMenus);
  const activeDayPartIndex = getActiveMenuIndex(
    sortedDayPartMenus,
    timezone,
    dayName,
    now
  );

  if (activeDayPartIndex !== -1) {
    sortedDayPartMenus[activeDayPartIndex].activeNow = true;
  } else {
    const upcomingDayPartIndex = getUpcomingMenuIndex(
      sortedDayPartMenus,
      timezone,
      now
    );
    if (!isNil(upcomingDayPartIndex) && upcomingDayPartIndex !== -1) {
      sortedDayPartMenus[upcomingDayPartIndex].upcoming = true;
    }
  }
  return sortedDayPartMenus;
};

const formatMenus = (dayPartMenus: DayPartMenu[], t: TFunction) => {
  let formattedDayParts: DayPart[] = [];

  dayPartMenus.forEach((menu) => {
    if (menu.dayParts.length > 0) {
      const dayParts = menu.dayParts.map((dayPart: DayPart) => {
        return {
          ...dayPart,
          formattedName: dayPart.name.replace(/[^a-zA-Z0-9]/g, ""),
          menuId: menu.id,
          menuName: decodeHtmlEntities(menu.name),
          availabilityLabel: getAvailabilityLabel(dayPart.days, t),
          activeNow: false,
          upcoming: false,
        };
      });

      formattedDayParts = [...formattedDayParts, ...dayParts];
    }
  });

  return formattedDayParts;
};

const updateMenusWithAvailabilityScore = (
  dayPartMenus: DayPart[],
  currentDayIndex: number
) => {
  const updatedMenus = dayPartMenus.map((menu) => {
    const availabilityScore = getAvailabilityScore(menu, currentDayIndex);
    return {
      ...menu,
      availabilityScore,
    };
  });

  return updatedMenus;
};

const getAvailabilityScore = (
  dayPartMenu: DayPart,
  currentDayIndex: number
) => {
  let minDaysFromToday = 7;

  for (const dayName of dayPartMenu.days) {
    const dayIndex = daysOfWeek.indexOf(dayName.toLowerCase() as WeekdayFull);
    if (dayIndex === -1) continue;

    const daysFromToday = (dayIndex - currentDayIndex + 7) % 7;
    if (daysFromToday < minDaysFromToday) {
      minDaysFromToday = daysFromToday;
    }
  }

  return minDaysFromToday;
};

const sort = (dayPartMenus: DayPart[]) => {
  return dayPartMenus.sort((a, b) => {
    if ((a.availabilityScore ?? 0) < (b.availabilityScore ?? 0)) return -1;
    if ((a.availabilityScore ?? 0) > (b.availabilityScore ?? 0)) return 1;

    const { startTime: startTimeA } = a.time;
    const { startTime: startTimeB } = b.time;

    return startTimeA - startTimeB;
  });
};

const getActiveMenuIndex = (
  dayPartMenus: DayPart[],
  timezone: string,
  dayName: string,
  now: DateTime<true> | DateTime<false>
) => {
  return dayPartMenus.findIndex((menu) => {
    if (!isEmpty(menu)) {
      const { days, time } = menu;

      if (
        days.includes(dayName.toUpperCase()) &&
        time?.startTime &&
        time?.endTime
      ) {
        const startTime = toDateTime(time.startTime.toString(), timezone);
        let endTime = toDateTime(time.endTime.toString(), timezone);

        // If end time is before start time, it means end time is next day
        if (endTime <= startTime) {
          endTime = endTime.plus({ days: 1 });
        }

        const interval = Interval.fromDateTimes(startTime, endTime);
        return interval.contains(now);
      }
    }
  });
};

const getUpcomingMenuIndex = (
  dayPartMenus: DayPart[],
  timezone: string,
  now: DateTime<true> | DateTime<false>
) => {
  const upcomingMenuIndexToday = dayPartMenus.findIndex((menu) => {
    if (!isEmpty(menu) && !isNil(menu.availabilityScore)) {
      const { time } = menu;
      if (menu.availabilityScore === 0) {
        const startTime = toDateTime(time.startTime.toString(), timezone);
        let endTime = toDateTime(time.endTime.toString(), timezone);

        // If end time is before start time, it means end time is next day
        if (endTime <= startTime) {
          endTime = endTime.plus({ days: 1 });
        }

        const interval = Interval.fromDateTimes(startTime, endTime);
        return interval.isAfter(now);
      }
    }
  });

  if (upcomingMenuIndexToday !== -1) return upcomingMenuIndexToday;

  for (let i = 1; i < 6; i++) {
    const date = now.plus({ days: i });
    const dayName = daysOfWeek[date.weekday % 7];
    const upcomingMenuIndex = dayPartMenus.findIndex((menu) => {
      if (!isEmpty(menu.days)) {
        if (menu.days.includes(dayName.toUpperCase())) {
          return true;
        }
      }
    });

    if (upcomingMenuIndex !== -1) return upcomingMenuIndex;
  }
};

export const getDayPartMenuAvailableTime = (
  menu: DayPart,
  timezone: string
) => {
  if (!isEmpty(menu?.time)) {
    const { startTime, endTime } = menu.time;
    const start = HHMMtoDate(
      startTime.toString().padStart(4, "0"),
      timezone,
      new Date()
    );
    const end = HHMMtoDate(
      endTime.toString().padStart(4, "0"),
      timezone,
      new Date()
    );
    return `${start.toLocaleString({
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })} - ${end.toLocaleString({
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  return "";
};

const getAvailabilityLabel = (days: string[], t: TFunction): string => {
  const daySet = new Set(days);
  const dayCount = daySet.size;

  if (dayCount === 7) {
    return t("DayPartMenu.everyday");
  }

  if (dayCount === 2 && daySet.has("SATURDAY") && daySet.has("SUNDAY")) {
    return t("DayPartMenu.weekendsOnly");
  }

  if (dayCount === 1) {
    const day = days[0];
    const capitalizedDay = t("DaysOfWeek." + day.toLowerCase());
    return t("DayPartMenu.daysOnly", { day: capitalizedDay });
  }

  if (dayCount === 2) {
    const isWeekendOnly = days.every(
      (day) => day === "SATURDAY" || day === "SUNDAY"
    );
    if (!isWeekendOnly) {
      const abbreviations = days
        .map((day) => t("DaysLongAbbreviated." + day.toLowerCase()))
        .filter(Boolean);
      if (abbreviations.length === 2) {
        return t("DayPartMenu.twoDaysOnly", {
          day1: abbreviations[0],
          day2: abbreviations[1],
        });
      }
    }
  }

  if (dayCount >= 3) {
    const sortedDays = ALL_DAYS.filter((day: string) => daySet.has(day));
    const dayAbbreviations = sortedDays
      .map((day: string) => t("DaysShortAbbreviated." + day.toLowerCase()))
      .join(", ");
    return t("DayPartMenu.multipleDaysOnly", { days: dayAbbreviations });
  }

  // Fallback for other cases (e.g., 2 weekend days not together, or other unhandled combos)
  const sortedDays = ALL_DAYS.filter((day: string) => daySet.has(day));
  const dayAbbreviations = sortedDays
    .map((day: string) => t("DaysLongAbbreviated." + day.toLowerCase()))
    .join(", ");
  return t("DayPartMenu.multipleDaysOnly", { days: dayAbbreviations });
};

export const getMenuDisplayName = (
  name: string | undefined | null,
  t: TFunction
): string => {
  if (!name) {
    return "";
  }
  const trimmedName = name.trim();
  if (trimmedName.toLowerCase().endsWith(t("menu"))) {
    return trimmedName;
  }
  return `${trimmedName} ${t("menu")}`;
};

export const findNextAvailableDay = (
  dayPartMenu: DayPart | null,
  timezone: string
) => {
  if (
    isEmpty(dayPartMenu) ||
    isEmpty(dayPartMenu.days) ||
    isEmpty(dayPartMenu.time)
  )
    return "";

  const now = DateTime.now().setZone(timezone);
  const todayIndex = now.weekday % 7;
  const dayName = daysOfWeek[now.weekday % 7];

  const menuAvailableDays = new Set(dayPartMenu.days);

  const startTime = toDateTime(dayPartMenu.time.startTime.toString(), timezone);

  if (menuAvailableDays.has(dayName.toUpperCase()) && startTime > now)
    return "";

  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (todayIndex + i) % 7;
    const nextDayName = daysOfWeek[nextDayIndex];

    if (menuAvailableDays.has(nextDayName.toUpperCase())) {
      return (
        nextDayName.charAt(0).toUpperCase() + nextDayName.slice(1).toLowerCase()
      );
    }
  }
};

export async function processDayParts(
  isDayPartsEnabled: boolean | undefined,
  merchantId: string,
  timezone: string,
  t: any,
  menuName: string | undefined,
  isDineIn: boolean,
  searchParams: any
) {
  if (!isDayPartsEnabled) {
    return [];
  }

  const menus = await getDayPartMenus(merchantId);
  if (isEmpty(menus)) {
    return [];
  }

  const dayPartMenus = sortAndUpdateDayPartMenus(menus, timezone, t);
  const queryParams = new URLSearchParams(searchParams);

  if (isDineIn) {
    handleDineInMenuRedirection(dayPartMenus, menuName, queryParams);
  } else {
    handleTakeoutMenuRedirection(dayPartMenus, menuName, queryParams);
  }

  return dayPartMenus;
}

function handleDineInMenuRedirection(
  dayPartMenus: DayPart[],
  menuName: string | undefined,
  queryParams: URLSearchParams
) {
  const activeMenu = dayPartMenus.find(
    (menu) => menu.activeNow || menu.upcoming
  );
  if (activeMenu && activeMenu.formattedName !== menuName) {
    queryParams.set("menu", activeMenu.formattedName);
    redirect(`/menu/all?${queryParams.toString()}`);
  }
}
function handleTakeoutMenuRedirection(
  dayPartMenus: DayPart[],
  menuName: string | undefined,
  queryParams: URLSearchParams
) {
  const hasValidMenu = menuName
    ? dayPartMenus.some((m) => m.formattedName === menuName)
    : false;

  if (!hasValidMenu) {
    const fallbackMenu = dayPartMenus.find((m) => m.activeNow || m.upcoming);

    if (fallbackMenu) {
      queryParams.set("menu", fallbackMenu.formattedName);
      redirect(`/menu/all?${queryParams.toString()}`);
    }
  }
}
